import React, { FC, useEffect, useState } from 'react';
import { LandscapeProjectsAddProjectsImpactTargetTemplate } from '../../templates';
import { ProjectTargetSaveRequestDto } from '../../../store/landscapeAPI/project/dto/request/project-target-save-request.dto';
import { useParams } from 'react-router-dom';
import { ProjectTargetResponseDto } from '../../../store/landscapeAPI/project/dto/response/project-target-response.dto';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch, RootState } from '../../../store/store';
import {
  deleteProjectTarget,
  fetchProjectTargetsByProjectId,
  saveProjectTarget,
  updateProjectTarget,
} from '../../../store/landscapeAPI/project/reducers/project-target.slice';
import Spinner from '../../atoms/Spinner/Spinner';
import cn from 'classnames';
import styles from './LandscapeProjectsAddProjectsImpactPage.module.css';
import { getUUID } from '../../../utils/UUID';
import { ProjectTargetUpdateRequestDto } from '../../../store/landscapeAPI/project/dto/request/project-target-update-request.dto';
import LandscapeProjectNotSavedInfoPage from '../LandscapeProjectNotSavedInfoPage';

interface LandscapeProjectsAddProjectsImpactPageProps {}

const LandscapeProjectsAddProjectsImpactPage: FC<
  LandscapeProjectsAddProjectsImpactPageProps
> = () => {
  const { id, projectId } = useParams();
  const dispatch = useDispatch<AppDispatch>();

  const [data, setData] = useState<ProjectTargetResponseDto[]>();

  const { projectTargets, status } = useSelector(
    (state: RootState) => state.projectTargetSlice
  );

  useEffect(() => {
    if (projectId && projectId !== 'new-project') {
      dispatch(fetchProjectTargetsByProjectId(projectId))
        .unwrap()
        .then((resp) => setData(resp));
    }
  }, [dispatch, projectId]);

  const onReorder = async (
    items: ProjectTargetResponseDto[],
    from: number,
    to: number
  ) => {
    setData(items);

    let firstInd =
      projectTargets?.findIndex(
        (item: ProjectTargetResponseDto) => item.sortOrder === from
      ) ?? -1;
    let secondInd =
      projectTargets?.findIndex(
        (item: ProjectTargetResponseDto) => item.sortOrder === to
      ) ?? -1;

    if (firstInd !== -1 && secondInd !== -1 && projectTargets && projectId) {
      await dispatch(
        updateProjectTarget({
          projectId: projectTargets[firstInd].projectId,
          label: projectTargets[firstInd].label,
          value: projectTargets[firstInd].value,
          sortOrder: to.toString(),
          id: projectTargets[firstInd].id,
          published: projectTargets[firstInd].published,
        })
      );
      await dispatch(
        updateProjectTarget({
          projectId: projectTargets[secondInd].projectId,
          label: projectTargets[secondInd].label,
          value: projectTargets[secondInd].value,
          sortOrder: from.toString(),
          id: projectTargets[secondInd].id,
          published: projectTargets[secondInd].published,
        })
      );
    }
  };

  const handleOnChange = (updatedProjectTarget: ProjectTargetResponseDto) => {
    setData((prevData) =>
      prevData?.map((projectTarget) =>
        projectTarget.id === updatedProjectTarget.id
          ? updatedProjectTarget
          : projectTarget
      )
    );
  };

  const handleAddTarget = async () => {
    const newProjectTarget: ProjectTargetSaveRequestDto = {
      projectId: projectId || '',
      label: '',
      value: '',
      id: getUUID(),
      published: true,
    };
    await dispatch(saveProjectTarget(newProjectTarget));
    if (projectId) {
      dispatch(fetchProjectTargetsByProjectId(projectId))
        .unwrap()
        .then((resp) => setData(resp));
    }
  };

  const handleDelete = async (projectTargetId: string) => {
    if (id) {
      await dispatch(
        deleteProjectTarget({ existingId: projectTargetId, compactId: id })
      );
    }
    if (projectId) {
      dispatch(fetchProjectTargetsByProjectId(projectId))
        .unwrap()
        .then((resp) => setData(resp));
    }
  };

  const handleSubmit = async () => {
    for (const item of data ?? []) {
      const existingItem = projectTargets?.find(
        (projectTarget) => projectTarget.id === item.id
      );
      if (
        item.label !== existingItem?.label ||
        item.value !== existingItem.value
      ) {
        const request: ProjectTargetUpdateRequestDto = {
          projectId: item.projectId,
          label: item.label,
          value: item.value,
          id: item.id,
          published: item.published,
          sortOrder: item.sortOrder.toString(),
        };

        await dispatch(updateProjectTarget(request));
        if (projectId) {
          await dispatch(fetchProjectTargetsByProjectId(projectId));
        }
      }
    }
  };

  if (status === 'loading') {
    return <Spinner />;
  }

  return (
    <>
      {data && projectId !== 'new-project' ? (
        <div className={cn(styles.wrapper)}>
          <LandscapeProjectsAddProjectsImpactTargetTemplate
            listItems={data}
            setListItems={onReorder}
            handleDelete={handleDelete}
            handleSubmit={handleSubmit}
            handleOnChange={handleOnChange}
            handleAddTarget={handleAddTarget}
          />
        </div>
      ) : (
        <LandscapeProjectNotSavedInfoPage />
      )}
    </>
  );
};

export default LandscapeProjectsAddProjectsImpactPage;
